import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Modal, Button, Spin, message, Input, Empty, Radio, Tooltip, Pagination } from "antd";
import {
  FolderOpenOutlined,
  FolderFilled,
  FileProtectOutlined,
  RightOutlined,
  LeftOutlined,
  HomeOutlined,
  SearchOutlined,
  DownloadOutlined,
  EyeOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  FileTextOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  VideoCameraOutlined,
  AudioOutlined,
  PaperClipOutlined,
  FormOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { healthsafety } from "@iso/services";
import { commonHelper } from "@iso/lib/helpers/commonHelper";
import { getFileIcon, getFileName } from "@iso/components/FileBrowser/fileUtils";
import { LAYOUT_TOPBAR_OFFSET } from "@iso/constants/layout";

// Page-mode heights: the panel fills below the topbar; the rail/body leave room
// for the topbar + the page header + a small buffer.
const PAGE_PANEL_MIN = `calc(100vh - ${LAYOUT_TOPBAR_OFFSET}px)`;
const PAGE_BODY_MIN = `calc(100vh - ${LAYOUT_TOPBAR_OFFSET + 160}px)`;
// Modal-mode body height: caps to the viewport (minus the modal title + margins)
// so the modal never overflows the screen and the category grid shows all rows
// without an outer scroll; the rail / main pane scroll internally if needed.
const MODAL_BODY_H = "min(600px, calc(100vh - 100px))";

// Per-tag category colour coding (matches the Figma folder cards).
// Exported so other Health & Safety surfaces (e.g. SafetyRecords) colour the
// same category identically.
export const FOLDER_STYLES = {
  documentation_training__policy: { fg: "#eb2f96", bg: "#fff0f6" },
  documentation_training__procedure: { fg: "#fa8c16", bg: "#fff7e6" },
  documentation_training__safe_operating_procedures: { fg: "#1890ff", bg: "#e6f7ff" },
  documentation_training__induction: { fg: "#722ed1", bg: "#f9f0ff" },
  documentation_training__job_specific_training: { fg: "#2f54eb", bg: "#f0f5ff" },
  documentation_training__risk_assessment: { fg: "#fa541c", bg: "#fff2e8" },
  documentation_training__job_safety_analysis: { fg: "#d4b106", bg: "#feffe6" },
  documentation_training__permit_to_work: { fg: "#13c2c2", bg: "#e6fffb" },
  documentation_training__lock_out_tag_out: { fg: "#c41d7f", bg: "#fff0f6" },
  documentation_training__safety_data_sheet_sds_location: { fg: "#389e0d", bg: "#f6ffed" },
  documentation_training__other: { fg: "#8c8c8c", bg: "#f5f5f5" },
};
export const FOLDER_DEFAULT_STYLE = { fg: "#067647", bg: "#f0faf4" };
const DEFAULT_STYLE = FOLDER_DEFAULT_STYLE;
const styleFor = (type) => FOLDER_STYLES[type] || DEFAULT_STYLE;

// Colour-coded icon per attachment file type.
const FILE_TYPE_STYLE = {
  image: { icon: <FileImageOutlined />, fg: "#389e0d", bg: "#f6ffed" },
  video: { icon: <VideoCameraOutlined />, fg: "#722ed1", bg: "#f9f0ff" },
  audio: { icon: <AudioOutlined />, fg: "#d48806", bg: "#fffbe6" },
  pdf: { icon: <FilePdfOutlined />, fg: "#cf1322", bg: "#fff1f0" },
  document: { icon: <FileWordOutlined />, fg: "#1890ff", bg: "#e6f7ff" },
};
const fileStyle = (t) => FILE_TYPE_STYLE[t] || { icon: <FileTextOutlined />, fg: "#1890ff", bg: "#e6f7ff" };

const fmtDate = (d) => (d ? moment(d).format("DD MMM YYYY") : "");

// Assets/forms per page in the category view.
const PER_PAGE = 24;

// Safety Documents pop-up.
//
// A browser of the tagged Health & Safety ASSETS (not raw files): the left rail
// / root cards are the Documentation & Training categories; opening a category
// lists the assets carrying that tag; opening an asset shows its attachments.
const SafetyDocumentsModal = ({ visible, onCancel, propertyId, asPage = false }) => {
  // As a full page the browser is always "open"; as a modal it follows `visible`.
  const isOpen = asPage || visible;
  const corporateGroupId = useMemo(() => {
    const active = commonHelper.getActiveProperty();
    if (!active || active.id !== propertyId) return null;
    return active.corporate_group_id || null;
  }, [propertyId]);
  const isCorporate = Boolean(corporateGroupId);

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [activeType, setActiveType] = useState(null); // null = root
  const [assets, setAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [activeAsset, setActiveAsset] = useState(null); // null = category list
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [viewMode, setViewMode] = useState("grid");
  const [detailTab, setDetailTab] = useState("details"); // 'details' | 'files'

  // Opening an asset/form lands on its Details, not straight on its files.
  useEffect(() => {
    setDetailTab("details");
    setSearch("");
  }, [activeAsset]);

  // As a page the component stays mounted across property switches (the modal
  // reset this via destroyOnClose). Reset the browser to the root so an open
  // asset-detail view doesn't keep showing the previous property's asset.
  useEffect(() => {
    setActiveType(null);
    setActiveAsset(null);
    setSearch("");
    setDebouncedSearch("");
    setPage(1);
  }, [propertyId]);

  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  const activeCategory = useMemo(
    () => categories.find((c) => c.type === activeType) || null,
    [categories, activeType]
  );

  // Load the categories (with asset counts) when the modal opens.
  useEffect(() => {
    if (!isOpen || !propertyId) return undefined;
    let cancelled = false;
    setLoading(true);
    healthsafety
      .getSafetyDocuments(propertyId, corporateGroupId)
      .then((res) => {
        if (!cancelled) setCategories((res && res.categories) || []);
      })
      .catch((err) => {
        console.error("Failed to load Safety Documents categories", err);
        if (!cancelled) message.error("Could not load Safety Documents. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, propertyId, corporateGroupId]);

  // Guards against out-of-order responses: a fast search / category switch can
  // have two fetches in flight, and the older one must not overwrite the newer.
  const reqIdRef = useRef(0);
  const loadAssets = useCallback(
    (type, pageArg, searchArg) => {
      const reqId = ++reqIdRef.current;
      setAssetsLoading(true);
      healthsafety
        .getSafetyDocumentAssets(type, propertyId, corporateGroupId, pageArg, PER_PAGE, searchArg)
        .then((res) => {
          if (reqId !== reqIdRef.current) return; // a newer request superseded this one
          setAssets((res && res.assets) || []);
          setTotal((res && res.total) || 0);
        })
        .catch((err) => {
          if (reqId !== reqIdRef.current) return;
          console.error("Failed to load Safety Documents items", err);
          message.error("Could not load these documents. Please try again.");
          setAssets([]);
          setTotal(0);
        })
        .finally(() => {
          if (reqId === reqIdRef.current) setAssetsLoading(false);
        });
    },
    [propertyId, corporateGroupId]
  );

  // Debounce the search box; a new term returns to the first page.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // Fetch the current page whenever the category / page / (debounced) search
  // changes, but only in the category list view — the asset-detail view filters
  // its own (already-loaded) attachments locally.
  useEffect(() => {
    if (!isOpen || !activeType || activeAsset) return;
    loadAssets(activeType, page, debouncedSearch);
  }, [isOpen, activeType, activeAsset, page, debouncedSearch, loadAssets]);

  const openCategory = useCallback((type) => {
    setActiveType(type);
    setActiveAsset(null);
    setSearch("");
    setDebouncedSearch("");
    setPage(1);
  }, []);

  const backToRoot = () => {
    setActiveType(null);
    setActiveAsset(null);
    setSearch("");
    setDebouncedSearch("");
    setPage(1);
  };
  const backToCategory = () => {
    setActiveAsset(null);
    setSearch("");
    setDebouncedSearch("");
  };

  const handleFileAction = (action, file) => {
    if (action === "preview") {
      setPreviewFile(file);
      setPreviewVisible(true);
    } else if (action === "download" && file.full_path) {
      window.open(file.full_path, "_blank", "noopener,noreferrer");
    }
  };

  const filteredAttachments = useMemo(() => {
    if (!activeAsset) return [];
    const q = search.trim().toLowerCase();
    const list = activeAsset.attachments || [];
    if (!q) return list;
    return list.filter((a) => getFileName(a).toLowerCase().includes(q));
  }, [activeAsset, search]);

  const renderPreviewContent = () => {
    if (!previewFile) return null;
    const name = getFileName(previewFile);
    if (previewFile.type === "image" && previewFile.full_path) {
      return (
        <div style={{ textAlign: "center" }}>
          <img src={previewFile.full_path} alt={name} style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }} />
        </div>
      );
    }
    if (previewFile.type === "pdf" && previewFile.full_path) {
      return <iframe src={previewFile.full_path} title={name} style={{ width: "100%", height: "70vh", border: "none" }} />;
    }
    return (
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <div style={{ fontSize: 64, marginBottom: 16, color: "#999" }}>{getFileIcon(previewFile.type)}</div>
        <p style={{ fontSize: 16 }}>{name}</p>
        {previewFile.full_path && (
          <Button type="primary" icon={<DownloadOutlined />} onClick={() => handleFileAction("download", previewFile)}>
            Download
          </Button>
        )}
      </div>
    );
  };

  const railItem = (key, label, active, onClick, count) => (
    <div
      key={key}
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 12px",
        borderRadius: 8,
        cursor: "pointer",
        fontWeight: active ? 600 : 400,
        color: active ? "#067647" : "#595959",
        background: active ? "#e6f4ea" : "transparent",
        marginBottom: 2,
      }}
    >
      <FolderOpenOutlined />
      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
      {typeof count === "number" ? <span style={{ color: "#bfbfbf", fontSize: 12 }}>{count}</span> : null}
    </div>
  );

  // ---- Category card (root) --------------------------------------------------
  const categoryCard = (cat) => {
    const s = styleFor(cat.type);
    return (
      <div
        key={cat.type}
        onClick={() => openCategory(cat.type)}
        className="sd-hover-card"
        style={{ border: "1px solid #f0f0f0", borderRadius: 12, padding: 12, cursor: "pointer", background: "#fff", display: "flex", flexDirection: "column", gap: 6 }}
      >
        <div style={{ width: 40, height: 36, borderRadius: 10, background: s.bg, color: s.fg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
          <FolderFilled />
        </div>
        <div style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.3 }}>{cat.name}</div>
        <div style={{ color: "#8c8c8c", fontSize: 12 }}>
          {cat.count} item{cat.count === 1 ? "" : "s"}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 2 }}>
          <span style={{ background: s.bg, color: s.fg, borderRadius: 6, padding: "1px 8px", fontSize: 11, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "80%" }}>{cat.name}</span>
          <RightOutlined style={{ color: "#bfbfbf", fontSize: 12 }} />
        </div>
      </div>
    );
  };

  // ---- Asset card / row ------------------------------------------------------
  const kindBadge = (item) => {
    const isForm = item.kind === "form";
    return (
      <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 6, padding: "1px 8px", background: isForm ? "#e6f7ff" : "#f0faf4", color: isForm ? "#1890ff" : "#067647" }}>
        {isForm ? "Form" : "Asset"}
      </span>
    );
  };

  const assetCard = (asset) => {
    const s = styleFor(asset.type);
    const isForm = asset.kind === "form";
    return (
      <div
        key={`${asset.kind}-${asset.id}`}
        onClick={() => setActiveAsset(asset)}
        className="sd-hover-card"
        style={{ border: "1px solid #eee", borderRadius: 12, padding: 14, cursor: "pointer", background: "#fff", display: "flex", flexDirection: "column", gap: 8 }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: s.bg, color: s.fg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>
            {isForm ? <FormOutlined /> : <FileProtectOutlined />}
          </div>
          {kindBadge(asset)}
        </div>
        <div style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {asset.title || (isForm ? "Untitled form" : "Untitled asset")}
        </div>
        <div style={{ color: "#8c8c8c", fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
          <PaperClipOutlined /> {asset.attachment_count} file{asset.attachment_count === 1 ? "" : "s"}
        </div>
        <div style={{ color: "#bfbfbf", fontSize: 12 }}>{fmtDate(asset.updated_at || asset.created_at)}</div>
      </div>
    );
  };

  const assetRow = (asset) => {
    const isForm = asset.kind === "form";
    return (
      <div
        key={`${asset.kind}-${asset.id}`}
        onClick={() => setActiveAsset(asset)}
        className="sd-hover-row"
        style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 8, cursor: "pointer", background: "#fff", marginBottom: 8, border: "1px solid #eee" }}
      >
        {isForm ? <FormOutlined style={{ color: "#1890ff", fontSize: 18 }} /> : <FileProtectOutlined style={{ color: styleFor(asset.type).fg, fontSize: 18 }} />}
        <div style={{ flex: 1, fontWeight: 600, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{asset.title || (isForm ? "Untitled form" : "Untitled asset")}</div>
        {kindBadge(asset)}
        <div style={{ color: "#8c8c8c", fontSize: 13, whiteSpace: "nowrap" }}>{asset.attachment_count} file{asset.attachment_count === 1 ? "" : "s"}</div>
        <div style={{ color: "#bfbfbf", fontSize: 13, whiteSpace: "nowrap", width: 110, textAlign: "right" }}>{fmtDate(asset.updated_at || asset.created_at)}</div>
        <RightOutlined style={{ color: "#bfbfbf", fontSize: 12 }} />
      </div>
    );
  };

  // ---- Attachment card / row -------------------------------------------------
  const attachmentCard = (file) => {
    const s = fileStyle(file.type);
    return (
      <div
        key={file.id}
        onClick={() => handleFileAction("preview", file)}
        className="sd-hover-card sd-file"
        style={{ border: "1px solid #eee", borderRadius: 12, padding: 14, cursor: "pointer", background: "#fff", display: "flex", flexDirection: "column", gap: 8, position: "relative" }}
      >
        <div className="sd-file-actions">
          <Button type="text" size="small" icon={<EyeOutlined />} onClick={(e) => { e.stopPropagation(); handleFileAction("preview", file); }} />
          {file.full_path && <Button type="text" size="small" icon={<DownloadOutlined />} onClick={(e) => { e.stopPropagation(); handleFileAction("download", file); }} />}
        </div>
        <div style={{ width: 44, height: 44, borderRadius: 10, background: s.bg, color: s.fg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>{s.icon}</div>
        <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{getFileName(file)}</div>
        <div style={{ color: "#bfbfbf", fontSize: 12 }}>{fmtDate(file.created_at)}</div>
        {file.user && (
          <div style={{ color: "#595959", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <EyeOutlined style={{ opacity: 0 }} />
            {`${file.user.name || ""} ${file.user.surname || ""}`.trim() || "User"}
          </div>
        )}
      </div>
    );
  };

  const attachmentRow = (file) => (
    <div
      key={file.id}
      onClick={() => handleFileAction("preview", file)}
      className="sd-hover-row"
      style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 8, cursor: "pointer", background: "#fff", marginBottom: 8, border: "1px solid #eee" }}
    >
      <span style={{ color: fileStyle(file.type).fg, fontSize: 18 }}>{fileStyle(file.type).icon}</span>
      <div style={{ flex: 1, fontWeight: 600, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{getFileName(file)}</div>
      <div style={{ color: "#bfbfbf", fontSize: 13, whiteSpace: "nowrap", width: 110, textAlign: "right" }}>{fmtDate(file.created_at)}</div>
      {file.full_path && <Button type="text" size="small" icon={<DownloadOutlined />} onClick={(e) => { e.stopPropagation(); handleFileAction("download", file); }} />}
    </div>
  );

  const gridWrap = (children) => (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16 }}>{children}</div>
  );

  // Toolbar shown in the category / asset views (title + search + view toggle + Back).
  const subToolbar = (titleNode, onBack) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 16, minWidth: 0 }}>{titleNode}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <Input
          placeholder="Search..."
          style={{ width: 220, maxWidth: "40vw" }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          prefix={<SearchOutlined style={{ color: "#8F92A1" }} />}
          allowClear
        />
        <Radio.Group value={viewMode} onChange={(e) => setViewMode(e.target.value)} buttonStyle="solid" size="middle">
          <Radio.Button value="grid"><Tooltip title="Grid view"><AppstoreOutlined /></Tooltip></Radio.Button>
          <Radio.Button value="list"><Tooltip title="List view"><UnorderedListOutlined /></Tooltip></Radio.Button>
        </Radio.Group>
        <Button icon={<LeftOutlined />} onClick={onBack}>Back</Button>
      </div>
    </div>
  );

  const greyArea = (children) => (
    <div style={{ background: "#f5f6f8", borderRadius: 12, padding: 16, minHeight: 460 }}>{children}</div>
  );

  const detailRow = (label, value) =>
    value === null || value === undefined || value === "" ? null : (
      <div style={{ display: "flex", gap: 12, padding: "8px 0", borderBottom: "1px solid #f5f5f5" }}>
        <div style={{ width: 150, color: "#8c8c8c", fontSize: 13, flexShrink: 0 }}>{label}</div>
        <div style={{ flex: 1, fontSize: 13, fontWeight: 500, whiteSpace: "pre-wrap" }}>{value}</div>
      </div>
    );

  // Record details panel (shown before the attachments when an item is opened).
  const detailsPanel = (item) => {
    const isForm = item.kind === "form";
    const s = styleFor(item.type);
    return (
      <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 12, padding: 20, minHeight: 460 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={{ width: 48, height: 48, borderRadius: 10, background: s.bg, color: s.fg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>
            {isForm ? <FormOutlined /> : <FileProtectOutlined />}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{item.title || (isForm ? "Untitled form" : "Untitled asset")}</div>
            <div style={{ marginTop: 4 }}>{kindBadge(item)}</div>
          </div>
        </div>
        {detailRow("Type", isForm ? "Form" : "Asset")}
        {detailRow("Category", activeCategory ? activeCategory.name : "")}
        {detailRow("Description", item.description)}
        {detailRow("Attachments", `${item.attachment_count} file${item.attachment_count === 1 ? "" : "s"}`)}
        {detailRow("Created", fmtDate(item.created_at))}
        {detailRow("Last updated", fmtDate(item.updated_at))}
      </div>
    );
  };

  // Title block — used as the Modal title, or the page header when asPage.
  const headerBlock = (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <FileProtectOutlined style={{ color: "#067647" }} />
      <div>
        <div style={{ fontWeight: 700 }}>Safety Documents</div>
        <div style={{ fontSize: 12, color: "#8c8c8c", fontWeight: 400 }}>
          Documentation &amp; Training · {categories.length} categories
          {isCorporate ? " · Corporate Library" : ""}
        </div>
      </div>
    </div>
  );

  // The browser body (category rail + main pane), shared by the modal and the
  // full-page view. In page mode it grows to fill the viewport.
  const browserBody = (
    <>
      <style>{`
        .sd-hover-card{transition:box-shadow .15s ease,border-color .15s ease}
        .sd-hover-card:hover{box-shadow:0 4px 14px rgba(0,0,0,.08);border-color:#d9d9d9}
        .sd-hover-row:hover{border-color:#067647}
        .sd-file .sd-file-actions{position:absolute;top:8px;right:8px;display:flex;gap:2px;opacity:0;transition:opacity .15s ease}
        .sd-file:hover .sd-file-actions{opacity:1}
      `}</style>
        <Spin spinning={loading}>
          <div style={{ display: "flex", overflow: "hidden", ...(asPage ? { minHeight: PAGE_BODY_MIN } : { height: MODAL_BODY_H }) }}>
            {/* Category rail */}
            <div style={{ width: 240, borderRight: "1px solid #f0f0f0", padding: 12, overflowY: "auto", maxHeight: asPage ? PAGE_BODY_MIN : "100%" }}>
              {railItem("all", "All Documents", !activeType, backToRoot)}
              <div style={{ fontSize: 11, color: "#bfbfbf", textTransform: "uppercase", letterSpacing: 0.4, margin: "12px 8px 6px" }}>Categories</div>
              {categories.map((c) => railItem(c.type, c.name, activeType === c.type, () => openCategory(c.type), c.count))}
            </div>

            {/* Main pane */}
            <div style={{ flex: 1, padding: 16, minWidth: 0, overflowY: "auto" }}>
              {!activeType ? (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 16, marginBottom: 12 }}>
                    <HomeOutlined style={{ color: "#8c8c8c" }} /> All Documents
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 12 }}>
                    {categories.map((c) => categoryCard(c))}
                  </div>
                </>
              ) : !activeAsset ? (
                <>
                  {subToolbar(
                    <><HomeOutlined style={{ color: "#8c8c8c" }} /> <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{activeCategory ? activeCategory.name : ""}</span></>,
                    backToRoot
                  )}
                  {greyArea(
                    <Spin spinning={assetsLoading}>
                      {assets.length === 0 ? (
                        <Empty
                          description={debouncedSearch ? "No matching documents" : "No assets in this category"}
                          style={{ padding: "40px 0" }}
                        />
                      ) : viewMode === "grid" ? (
                        gridWrap(assets.map((a) => assetCard(a)))
                      ) : (
                        assets.map((a) => assetRow(a))
                      )}
                      {total > PER_PAGE && (
                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                          <Pagination
                            current={page}
                            pageSize={PER_PAGE}
                            total={total}
                            showSizeChanger={false}
                            onChange={(p) => setPage(p)}
                          />
                        </div>
                      )}
                    </Spin>
                  )}
                </>
              ) : (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, fontWeight: 700, fontSize: 16 }}>
                      <a onClick={backToRoot} style={{ color: "#067647", fontWeight: 400 }}>All Documents</a>
                      <span style={{ color: "#bfbfbf" }}>/</span>
                      <a onClick={backToCategory} style={{ color: "#067647", fontWeight: 400 }}>{activeCategory ? activeCategory.name : ""}</a>
                      <span style={{ color: "#bfbfbf" }}>/</span>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{activeAsset.title || "Asset"}</span>
                    </span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                      <Radio.Group value={detailTab} onChange={(e) => setDetailTab(e.target.value)} buttonStyle="solid" size="middle">
                        <Radio.Button value="details">Details</Radio.Button>
                        <Radio.Button value="files">Files ({activeAsset.attachment_count})</Radio.Button>
                      </Radio.Group>
                      <Button icon={<LeftOutlined />} onClick={backToCategory}>Back</Button>
                    </div>
                  </div>

                  {detailTab === "details" ? (
                    detailsPanel(activeAsset)
                  ) : (
                    <>
                      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginBottom: 12 }}>
                        <Input
                          placeholder="Search files..."
                          style={{ width: 220, maxWidth: "40vw" }}
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          prefix={<SearchOutlined style={{ color: "#8F92A1" }} />}
                          allowClear
                        />
                        <Radio.Group value={viewMode} onChange={(e) => setViewMode(e.target.value)} buttonStyle="solid" size="middle">
                          <Radio.Button value="grid"><Tooltip title="Grid view"><AppstoreOutlined /></Tooltip></Radio.Button>
                          <Radio.Button value="list"><Tooltip title="List view"><UnorderedListOutlined /></Tooltip></Radio.Button>
                        </Radio.Group>
                      </div>
                      {greyArea(
                        (activeAsset.attachments || []).length === 0 ? (
                          <Empty description="No attachments on this item" style={{ padding: "40px 0" }} />
                        ) : filteredAttachments.length === 0 ? (
                          <Empty description="No files match your search" style={{ padding: "40px 0" }} />
                        ) : viewMode === "grid" ? (
                          gridWrap(filteredAttachments.map((f) => attachmentCard(f)))
                        ) : (
                          filteredAttachments.map((f) => attachmentRow(f))
                        )
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </Spin>
    </>
  );

  const previewModal = (
    <Modal
      visible={previewVisible}
      title={previewFile ? getFileName(previewFile) : "Preview"}
      footer={null}
      onCancel={() => { setPreviewVisible(false); setPreviewFile(null); }}
      width={800}
      destroyOnClose
    >
      {renderPreviewContent()}
    </Modal>
  );

  // Full-page view (navigated from the Safety nav) — no modal chrome; fills the
  // whole active area (page wrapper cancels the layout's grey padding).
  if (asPage) {
    return (
      <div style={{ background: "#fff", minHeight: PAGE_PANEL_MIN }}>
        <div style={{ padding: "16px 24px", borderBottom: "1px solid #f0f0f0" }}>{headerBlock}</div>
        {browserBody}
        {previewModal}
      </div>
    );
  }

  return (
    <>
      <Modal
        visible={visible}
        onCancel={onCancel}
        footer={null}
        width={1352}
        centered
        bodyStyle={{ padding: 0 }}
        destroyOnClose
        title={headerBlock}
      >
        {browserBody}
      </Modal>
      {previewModal}
    </>
  );
};

export default SafetyDocumentsModal;
